import React from 'react';
import Box from '@mui/material/Box';
import { ArrowBack, Close as CloseIcon } from '@mui/icons-material';
import colors from '../../theme/colors.js';
import Text from '../Text.js';
import Button from '../Button.js';
import i18n from "../../localization/i18n.js";
import tokens from "../../localization/tokens.js";
import Loader from '../Loader.js';
import { useTheme } from '@mui/system';

/**
 * 
 * @returns 
 */
function getBrowserName() {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Firefox')) {
    return 'Firefox';
  }
  if (userAgent.includes('Chrome')) {
    return 'Chrome';
  }
  if (userAgent.includes('Safari')) {
    return 'Safari';
  }
  if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    return 'Internet Explorer';
  }
  if (userAgent.includes('Edge')) {
    return 'Edge';
  }
  return 'Unknown';
}

/**
 * @param children
 * @param open
 * @param medium
 * @param large
 * @param onBack
 * @param onClose
 * @param primaryButton
 * @param secondaryButton
 * @param title
 * @param loader
 * @param style
 * @returns {JSX.Element|null}
 * @constructor
 */
const Modal = ({
  children,
  open,
  medium,
  large,
  onBack,
  onClose,
  primaryButton,
  secondaryButton,
  title,
  loader = {},
  style = {}
}) => {
  const theme = useTheme();
  const browser = getBrowserName();
  const styles = {
    closeIcon: {
      color: colors.mediumGrey,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.1)' /* Black w/ opacity */
      }
    },
    backdrop: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      zIndex: 1000,
      position: 'fixed',
      left: 0,
      top: 0,
      overflow: 'auto',
      backgroundColor: 'rgba(0, 0, 0, 0.5)' /* Black w/ opacity */
    },
    modal: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#FFFFFF',
      borderRadius: 10,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      padding: "40px 45px 45px 45px",
      width: large ? '80%' : medium ? '50%' : '20%',
      minWidth: 500,
      minHeight: 160,
      maxHeight: '85%',
      overflow: 'auto',
      maxWidth: large ? "1950px" : medium ? "1000px" : "800px",
      ...style
    },
    titleBlock: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: 20
    },
    title: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    contentBlock: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      overflow: 'auto',
      paddingRight: 3,
      paddingBottom: 2,
      msOverflowStyle: 'none',
      ...(browser === 'Firefox' ? {
        scrollbarWidth: 'thin'
      } : null),
      // For Firefox
      '&::-webkit-scrollbar': {
        width: '5px'
      },
      '&::-webkit-scrollbar-track': {
        background: '#EEEEEE'
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#D1D3D4'
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#939598'
      }
    },
    buttonBlock: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      paddingTop: 20
    },
    goBackIcon: {
      cursor: 'pointer',
      fontSize: '22px',
      marginRight: 1.25,
      lineHeight: 20
    }
  };
  if (!open) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", {
    style: styles.backdrop
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.modal
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.titleBlock
  }, /*#__PURE__*/React.createElement("div", {
    style: styles.title
  }, onBack && /*#__PURE__*/React.createElement(ArrowBack, {
    onClick: onBack,
    sx: styles.goBackIcon
  }), /*#__PURE__*/React.createElement(Text, {
    large: true,
    bold: true,
    customStyle: {
      fontSize: "28px"
    }
  }, title)), onClose && /*#__PURE__*/React.createElement(CloseIcon, {
    onClick: onClose,
    sx: styles.closeIcon
  })), /*#__PURE__*/React.createElement(Box, {
    id: "modalContentBlock",
    sx: styles.contentBlock
  }, children), /*#__PURE__*/React.createElement("div", {
    style: styles.buttonBlock
  }, secondaryButton && /*#__PURE__*/React.createElement(Button, {
    primary: true,
    transparent: true,
    icon: secondaryButton.icon,
    color: secondaryButton.color || 'primary',
    onClick: secondaryButton.onClick,
    customStyle: {
      color: theme.palette.primary.main,
      marginRight: 1.25,
      minWidth: 100
    }
  }, secondaryButton.label), primaryButton && /*#__PURE__*/React.createElement(Button, {
    primary: true,
    icon: primaryButton.icon,
    color: primaryButton.color || 'primary',
    onClick: primaryButton.onClick,
    customStyle: {
      minWidth: 100
    }
  }, primaryButton.label || i18n.t(tokens.buttons.save), primaryButton.children)), loader?.isLoading && loader?.type === 'modal' ? /*#__PURE__*/React.createElement(Loader, {
    isModal: true
  }) : null));
};
export default Modal;